import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import './Help.css';

const helpTopics = [
  {
    icon: '🏆',
    title: 'Valg af casino',
    desc: 'Hvad skal jeg kigge efter, når jeg vælger et casino?',
    link: '/casino-guide',
    linkLabel: 'Læs vores casino guide'
  },
  {
    icon: '🔍',
    title: 'Vores anmeldelsesmetode',
    desc: 'Forstå, hvordan vi vurderer og rangerer casinoerne på denne side.',
    link: '/vores-metode',
    linkLabel: 'Se vores metode'
  },
  {
    icon: '🛡️',
    title: 'Ansvarligt spil',
    desc: 'Ressourcer, redskaber og vejledning til ansvarligt og sikkert spil.',
    link: '/ansvarligt-spil',
    linkLabel: 'Læs om ansvarligt spil'
  },
  {
    icon: '📋',
    title: 'Privatlivspolitik',
    desc: 'Hvordan behandler vi dine personoplysninger på TopCasinoDansk?',
    link: '/privatlivspolitik',
    linkLabel: 'Læs privatlivspolitik'
  },
  {
    icon: '🍪',
    title: 'Cookiepolitik',
    desc: 'Information om de cookies, vi bruger på vores hjemmeside.',
    link: '/cookiepolitik',
    linkLabel: 'Læs cookiepolitik'
  },
  {
    icon: '✉️',
    title: 'Kontakt redaktionen',
    desc: 'Har du spørgsmål eller feedback? Vi besvarer henvendelser inden for 2 hverdage.',
    link: '/kontakt',
    linkLabel: 'Gå til kontakt'
  },
];

const helpFaq = [
  {
    q: 'Udbyder TopCasinoDansk selv casinospil?',
    a: 'Nej. Vi er en uafhængig informations- og sammenligningsportal. Vi hverken udbyder spil, accepterer indskud eller er tilknyttet casinoerne som operatør.'
  },
  {
    q: 'Kan jeg stole på jeres anbefalinger?',
    a: 'Vores anmeldelser er baseret på egne redaktionelle kriterier og selvstændige tests. Vi omtaler kun casinoer med gyldig dansk licens. Vores affiliate-relationer oplyses tydeligt og påvirker ikke vores vurderinger.'
  },
  {
    q: 'Hvad er en dansk casinolicens?',
    a: 'En dansk casinolicens udstedes af Spillemyndigheden og giver tilladelse til at udbyde online casino til spillere i Danmark. Alle lovlige danske casinosider skal have denne licens.'
  },
  {
    q: 'Hvad gør jeg, hvis jeg vil stoppe med at spille?',
    a: 'Du kan selvudelukke dig fra alle danske spilsider via ROFUS.nu. Du kan også kontakte StopSpillet.dk for gratis og anonym rådgivning. Hjælpetelefonen er 70 22 28 25.'
  },
];

export default function Help() {
  return (
    <>
      <Helmet>
        <title>Hjælp & Support – TopCasinoDansk</title>
        <meta name="description" content="Find svar på dine spørgsmål om TopCasinoDansk, vores metode, ansvarligt spil og dansk casinoregulering." />
      </Helmet>

      <PageHero
        title="Hjælp & Support"
        subtitle="Find hurtigt svar på de mest stillede spørgsmål, eller navigér direkte til den information du leder efter."
        breadcrumb={<><Link to="/">Forside</Link> › Hjælp & Support</>}
      />

      {/* Help topics grid */}
      <section className="help-section section">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '0.75rem' }}>Hvad leder du efter?</h2>
          <p className="section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Vælg et emne nedenfor for at komme hurtigt til den relevante information.
          </p>

          <div className="help-topics-grid">
            {helpTopics.map((topic, i) => (
              <Link key={i} to={topic.link} className="help-topic-card">
                <div className="help-topic-icon">{topic.icon}</div>
                <div className="help-topic-content">
                  <h4>{topic.title}</h4>
                  <p>{topic.desc}</p>
                  <span className="help-topic-link">{topic.linkLabel} →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Help FAQ */}
      <section className="help-faq-section section bg-sand">
        <div className="container">
          <div className="divider"></div>
          <h2 style={{ marginBottom: '2rem' }}>Hyppige spørgsmål</h2>

          <div className="help-faq-grid">
            {helpFaq.map((item, i) => (
              <div key={i} className="help-faq-item">
                <h4>{item.q}</h4>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety block */}
      <section className="help-safety section">
        <div className="container">
          <div className="help-safety-grid">
            <div className="help-safety-block">
              <h3>🔒 Spillersikkerhed og ROFUS</h3>
              <p>
                ROFUS giver dig mulighed for at udelukke dig frivilligt fra alle licenserede danske spilsider.
                Registreringen er øjeblikkelig og gratis. Besøg <a href="https://www.rofus.nu/" target="_blank" rel="noopener noreferrer">ROFUS.nu</a> for at registrere dig.
              </p>
            </div>
            <div className="help-safety-block">
              <h3>💬 StopSpillet – Gratis hjælp</h3>
              <p>
                StopSpillet tilbyder gratis, anonym rådgivning til spillere med problemer og til pårørende.
                Ring på <strong>70 22 28 25</strong> eller besøg <a href="https://www.stopspillet.dk/" target="_blank" rel="noopener noreferrer">StopSpillet.dk</a>.
              </p>
            </div>
            <div className="help-safety-block">
              <h3>📋 Spillemyndigheden</h3>
              <p>
                Den danske myndighed, der regulerer og overvåger alle spiloperatører med dansk licens.
                Find information og klage over casinoer på <a href="https://www.spillemyndigheden.dk/" target="_blank" rel="noopener noreferrer">spillemyndigheden.dk</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <div className="help-contact-cta bg-sand">
        <div className="container">
          <div className="help-cta-inner">
            <div>
              <h4>Fandt du ikke svaret?</h4>
              <p>Vores redaktion hjælper dig gerne. Vi besvarer henvendelser inden for 2 hverdage.</p>
            </div>
            <Link to="/kontakt" className="btn btn-primary">Kontakt redaktionen</Link>
          </div>
        </div>
      </div>
    </>
  );
}
